import { Dispatch, SetStateAction, useState } from 'react';

import {
  Accordion,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  TextAreaInputV2,
  ToastMessage,
  useToastMessage,
} from '@equip.health/ui';

import {
  ProfileEventsAdmin,
  ProfileEventsMaud,
} from '~/analytics/profileEvents';
import {
  ACCORDION_HEIGHT_CALCULATED,
  ACCORDION_WIDTH_LARGE,
} from '~/lib/constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import { useUpdateProviderAboutMeByIdMutation } from '~/store/services/providers';

import DiscardChangesModalV2 from '../DiscardChangesModalV2';

interface EditAboutMeAccordionProps {
  onDismissEditAboutMeAccordion?: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  aboutMeData: GetProviderByIdResponse;
}

const TOP_LEVEL_ID = 'about-me-accordion';

const EditAboutMeAccordion = ({
  onDismissEditAboutMeAccordion,
  isExpanded,
  setIsExpanded,
  aboutMeData,
}: EditAboutMeAccordionProps) => {
  const [editProviderAboutMe, editProviderAboutMeResult] =
    useUpdateProviderAboutMeByIdMutation();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState<boolean>(false);
  const [isUnsavedAboutMeChanges, setIsUnsavedAboutMeChanges] =
    useState<boolean>(false);
  const [professionalExperience, setProfessionalExperience] = useState<string>(
    aboutMeData?.professionalExperience || '',
  );
  const [thingsToConnectWithMe, setThingsToConnectWithMe] = useState<string>(
    aboutMeData?.connectWithMe || '',
  );
  const isSubmitError = editProviderAboutMeResult.isError;
  const isLoading = editProviderAboutMeResult.isLoading;

  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();
  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;

  const CHARACTER_LIMIT = 750;

  const isProfessionalExperienceTextOverLimit =
    professionalExperience?.length > CHARACTER_LIMIT;
  const isConnectWithMeTextOverLimit =
    thingsToConnectWithMe?.length > CHARACTER_LIMIT;

  const onResetAndClose = (): void => {
    setIsUnsavedAboutMeChanges(false);
    setIsUnsavedChangesModalOpen(false);

    onDismissEditAboutMeAccordion();
  };

  const handleCancelAndClose = (): void => {
    if (isUnsavedAboutMeChanges || isSubmitError) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      onResetAndClose();
    }
  };
  const handleStateFieldChange =
    <T,>(setState: Dispatch<SetStateAction<T>>) =>
    (value: T) => {
      setIsUnsavedAboutMeChanges(true);
      setState(value);
    };
  const handleSave = () => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminAboutMeModalSaveChangesButtonClicked
        : ProfileEventsMaud.maudAboutMeModalSaveChangesButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    editProviderAboutMe({
      connectWithMe: thingsToConnectWithMe,
      professionalExperience,
      providerExternalId: aboutMeData?.userId,
    })
      .unwrap()
      .then(() => {
        setIsUnsavedAboutMeChanges(false);
        onDismissEditAboutMeAccordion();
        createToast({
          icon: FAIconName.checkRegular,
          id: 'save-edit-profile_toast',
          text: 'About me updated',
          variant: 'success',
        });
      })
      .catch(() => {
        setIsUnsavedAboutMeChanges(true);
      });
  };

  const footerContent = (
    <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel`}
        leftIcon={FAIconName.XmarkRegular}
        onClick={handleCancelAndClose}
        variant="secondary"
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__save`}
        isDisabled={
          !isUnsavedAboutMeChanges ||
          isProfessionalExperienceTextOverLimit ||
          isConnectWithMeTextOverLimit
        }
        isLoading={isLoading}
        leftIcon={FAIconName.checkRegular}
        onClick={handleSave}
      >
        Save changes
      </FireflyButton>
    </ChakraFlex>
  );

  return (
    <>
      <DiscardChangesModalV2
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={onResetAndClose}
      />
      <Accordion
        dismissMenu={handleCancelAndClose}
        footer={footerContent}
        id={`${TOP_LEVEL_ID}`}
        isExpanded={isExpanded}
        panelContentMaxHeight={ACCORDION_HEIGHT_CALCULATED}
        panelMaxWidth={ACCORDION_WIDTH_LARGE}
        panelMinWidth={ACCORDION_WIDTH_LARGE}
        setIsExpanded={setIsExpanded}
        title="Edit about me"
      >
        <ChakraFlex flexDirection="column" gap="24px" padding="24px">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__professional-experience`}
            isRequired={false}
            label="Professional experience"
            shouldHideOptionalLabel
            value={
              isProfessionalExperienceTextOverLimit
                ? 'Text exceeds 750 character limit'
                : ''
            }
          >
            <TextAreaInputV2
              id={`${TOP_LEVEL_ID}__professional-experience-input`}
              isInvalid={isProfessionalExperienceTextOverLimit}
              onChange={handleStateFieldChange<string>(
                setProfessionalExperience,
              )}
              value={professionalExperience}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__things-to-connect-with-me`}
            isRequired={false}
            label="Things to connect with me about"
            shouldHideOptionalLabel
            value={
              isConnectWithMeTextOverLimit
                ? 'Text exceeds 750 character limit'
                : ''
            }
          >
            <TextAreaInputV2
              id={`${TOP_LEVEL_ID}__things-to-connect-with-me-input`}
              isInvalid={isConnectWithMeTextOverLimit}
              onChange={handleStateFieldChange<string>(
                setThingsToConnectWithMe,
              )}
              value={thingsToConnectWithMe}
            />
          </InputGroupV2>
          {isSubmitError && (
            <ToastMessage
              icon={FAIconName.exclamationTriangleRegular}
              id={`${TOP_LEVEL_ID}__warning`}
              isFullWidth
              text="We're sorry, we were unable to save your changes. Please try
                  again."
              variant="critical"
            />
          )}
        </ChakraFlex>
      </Accordion>
    </>
  );
};

export default EditAboutMeAccordion;
